import type { HomeStateData } from '@workspace/api-backend'
import { PULSE_LABEL } from '../config/copy'

/**
 * This week's activity: sent, accepted, replies.
 *
 * Three lines only. The mockup also showed profile reads and evaluation opens,
 * but verified-human view data starts too recently to be trustworthy and the
 * evaluation-open event has no agreed mapping — so they are deliberately absent
 * rather than rendered as hopeful zeroes.
 *
 * Shown from the first send onwards, quiet weeks included: a campaign that has
 * sent before and has sent nothing this week is exactly what the founder came
 * to check, and hiding the strip answers that question by omission. Before the
 * first send there is no week to report, so it stays away — a row of zeroes on
 * a campaign that has never run reads as failure rather than as "not yet".
 */
export function HomePulseStrip({
  pulse,
  sentTotal,
}: {
  pulse: HomeStateData['pulse']
  /** Lifetime sends — whether there has ever been a week worth reporting. */
  sentTotal: number
}) {
  const stats = [
    { value: pulse.sent, label: 'sent' },
    { value: pulse.accepted, label: 'accepted' },
    { value: pulse.replies, label: pulse.replies === 1 ? 'reply' : 'replies' },
  ]

  if (sentTotal === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-border/40 pt-3 text-[12.5px] text-muted-foreground">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
        {PULSE_LABEL}
      </span>
      {stats.map((stat) => (
        <span key={stat.label}>
          <b className="font-semibold text-foreground tabular-nums">{stat.value}</b> {stat.label}
        </span>
      ))}
    </div>
  )
}
